import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom, take } from 'rxjs';
import { Organization, OrganizationProjects, OrganizationUsers, Project, ProjectUsers } from '@models';
import { WorkspaceActions, WorkspaceLoadPayload } from './workspace.actions';
import {
  selectActiveOrganizationId,
  selectActiveProject,
  selectActiveProjectId,
  selectAllOrgProjects,
  selectAllOrgUsers,
  selectAllOrganizations,
  selectAllProjectUsers,
  selectAllProjects,
  selectError,
  selectIsLoading,
  selectIsReady,
  selectSelectedOrganizationId,
  selectSelectedProjectId,
  selectStatus,
} from './workspace.selectors';

@Injectable({ providedIn: 'root' })
export class WorkspaceFacade {
  private store = inject(Store);
  private loadLock: Promise<void> | null = null;

  readonly projects$ = this.store.select(selectAllProjects);
  readonly organizations$ = this.store.select(selectAllOrganizations);
  readonly orgProjects$ = this.store.select(selectAllOrgProjects);
  readonly projectUsers$ = this.store.select(selectAllProjectUsers);
  readonly orgUsers$ = this.store.select(selectAllOrgUsers);

  readonly projects = toSignal(this.projects$, { initialValue: [] as Project[] });
  readonly organizations = toSignal(this.organizations$, { initialValue: [] as Organization[] });
  readonly orgProjects = toSignal(this.orgProjects$, { initialValue: [] as OrganizationProjects[] });
  readonly projectUsers = toSignal(this.projectUsers$, { initialValue: [] as ProjectUsers[] });
  readonly orgUsers = toSignal(this.orgUsers$, { initialValue: [] as OrganizationUsers[] });

  readonly activeProject$ = this.store.select(selectActiveProject);
  readonly activeProjectId$ = this.store.select(selectActiveProjectId);
  readonly activeProjectId = toSignal(this.store.select(selectActiveProjectId), { initialValue: null });
  readonly activeProject = toSignal(this.store.select(selectActiveProject), { initialValue: null });
  readonly activeOrganizationId$ = this.store.select(selectActiveOrganizationId);
  readonly activeOrganizationId = toSignal(this.store.select(selectActiveOrganizationId), { initialValue: null });
  readonly selectedProjectId = toSignal(this.store.select(selectSelectedProjectId), { initialValue: null });
  readonly selectedOrganizationId = toSignal(this.store.select(selectSelectedOrganizationId), { initialValue: null });
  readonly initialized = toSignal(this.store.select(selectIsReady), { initialValue: false });
  readonly isReady$ = this.store.select(selectIsReady);
  readonly isLoading$ = this.store.select(selectIsLoading);
  readonly error$ = this.store.select(selectError);

  private waitUntilSettled(): Promise<void> {
    return firstValueFrom(
      this.store.select(selectStatus).pipe(filter((s) => s === 'ready' || s === 'error'))
    ).then(() => undefined);
  }

  private runWorkspaceLoad(dispatch: () => void): Promise<void> {
    if (this.loadLock) return this.loadLock;

    this.loadLock = (async () => {
      dispatch();
      await this.waitUntilSettled();
    })().finally(() => {
      this.loadLock = null;
    });

    return this.loadLock;
  }

  async init(): Promise<void> {
    const status = await firstValueFrom(this.store.select(selectStatus).pipe(take(1)));
    if (status === 'ready') return;
    if (status === 'loading') {
      if (this.loadLock) await this.loadLock;
      else await this.waitUntilSettled();
      return;
    }
    await this.runWorkspaceLoad(() => this.store.dispatch(WorkspaceActions.init()));
  }

  async refreshWorkspaceData(): Promise<void> {
    await this.runWorkspaceLoad(() => this.store.dispatch(WorkspaceActions.reload()));
  }

  markInitialized(): void {
    /* no-op */
  }

  selectProject(projectId: string | null, organizationId?: string | null): void {
    this.store.dispatch(WorkspaceActions.selectProject({ projectId, organizationId }));
  }

  upsertEntities(entities: Partial<WorkspaceLoadPayload>): void {
    this.store.dispatch(WorkspaceActions.entitiesUpserted({ entities }));
  }

  removeEntities(entities: Partial<WorkspaceLoadPayload>): void {
    this.store.dispatch(WorkspaceActions.entitiesRemoved({ entities }));
  }

  reset(): void {
    this.store.dispatch(WorkspaceActions.reset());
  }
}
